import React, { useEffect, useMemo, useState } from "react";
import * as ReactDOMServer from 'react-dom/server';
import {
  useTable,
  useFilters,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  usePagination,
  useExpanded
} from "react-table";
import "bootstrap/dist/css/bootstrap.min.css";
import TableStyle from "components/Content/Table/TableStyle.module.css";
import { Box, Button, Chip, Grid, IconButton, InputAdornment, Popover, Stack, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from '@material-ui/icons/LastPage';
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import LoaderAnimation from "pages/LoaderAnimation";
import AximDropdown from "components/Dropdown/AximDropdown";
import { Utils } from "common/Utils";
import AximTooltip from "components/AximTooltip";
import DownloadIcon from '@mui/icons-material/CloudDownload';
import { createCSVDownload, downloadCSV } from 'components/mui-datatable/utils';
import { apiPost } from "app/Services/ApiService";
import { apiUrls } from "common/ApiUrls";
import moment from "moment";

// Define a default UI for filtering
function GlobalFilter ({
  headerContents,
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
  columns,
  handleCSVDownload,
  download
}) {
  const count = preGlobalFilteredRows.length;
  const DownloadIconComponent = DownloadIcon;
  const [value, setValue] = React.useState(globalFilter);
  const [showSearch, setShowSearch] = useState(false);
  const [filterValues, setFilterValues] = useState([]);

  const [anchorEl, setAnchorEl] = useState(null);

  const search = (value) => {
    onChange(value);
    setValue(value);
  }

  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  const allValues = useMemo(() => {
    var allValues = [];
    for (var c = 0; c < columns.length; c++) {
      if (columns[c].canFilter) {
        var colValues = preGlobalFilteredRows.map(p => (Array.isArray(p.values[columns[c].id]) ? p.values[columns[c].id][0] : p.values[columns[c].id]))
          .filter(Utils.onlyUnique)
          .sort((a, b) => {
            if (Date.parse(a) !== NaN) {
              a = Date.parse(a);
              b = Date.parse(b);
            }
            return !isNaN(a) ? a - b : (a < b ? -1 : a > b ? 1 : 0);
          }); // sort numbers numerically and strings/dates stringwise
        var value = {
          id: columns[c].id,
          header: columns[c].Header,
          values: colValues,
          column: columns[c],
        };
        allValues.push(value);
      }
    }

    // console.log(allValues, "allValues");

    return allValues;
  }, [preGlobalFilteredRows, columns]);

  useEffect(() => {
    search(undefined);
  }, [showSearch])

  const changeFilter = (value, column) => {
    column.setFilter(value || undefined);

    if (filterValues) {
      var index = filterValues.findIndex(v => v.id === column.id);

      if (index > -1) {
        filterValues.splice(index, 1);
      }

      if (value) {
        setFilterValues([...filterValues, { id: column.id, value: value }]);
      }
    }
  }

  const handleDelete = (id) => {
    var value = allValues.find(v => v.id === id);

    if (value) {
      value.column.setFilter('');
    }

    var index = filterValues.findIndex(v => v.id === id);

    if (index > -1) {
      filterValues.splice(index, 1)
      setFilterValues([...filterValues]);
    }
  }

  const clearFilters = () => {
    for (var v = 0; v < allValues.length; v++) {
      if (allValues[v].column && allValues[v].column.setFilter) {
        allValues[v].column.setFilter('');
      }
    }

    setFilterValues([]);
  }
  return (
    <>
      <Grid key="a" container justifyContent="space-between" className="what">
        <Box key="header" className="px-2" sx={{ display: "flex", alignItems: "center" }}>{headerContents}</Box>
        {/* <div className={TableStyle.tableActionWrapper}> */}
        <Box key="filters">
          <Box>
            <Box className="d-flex">
              <TextField
                className={showSearch ? "" : "d-none"}
                inputRef={input => input && input.focus()}
                value={value || ""}
                onChange={e => search(e.target.value)}
                color="primary"
                label="Search"
                variant="standard"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="primary" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={e => search(undefined)}>
                        <ClearIcon color="primary" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <AximTooltip title="Search">
                <IconButton onClick={() => setShowSearch(prev => !prev)} >
                  <SearchIcon />
                </IconButton>
              </AximTooltip>
              {download ?
                <AximTooltip title="Download">
                  <IconButton
                    onClick={handleCSVDownload}
                  >
                    <DownloadIconComponent />
                  </IconButton>
                </AximTooltip> : <></>}
              <Box>
                <AximTooltip title="Filter Table">
                  <IconButton onClick={e => setAnchorEl(e.currentTarget)}>
                    <FilterListIcon />
                  </IconButton>
                </AximTooltip>
              </Box>
            </Box>
          </Box>

          <Popover
            id="profile-menu"
            anchorEl={anchorEl}
            // keepMounted
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <Box
              className="m-2 container"
              style={{ maxWidth: "480px" }}
            >
              <Box className="row">
                <Box className="col-12 d-flex" style={{ justifyContent: "space-between" }}>
                  <Box component="h4" className="me-2">Filters</Box>
                  <Button
                    variant="outlined"
                    onClick={() => clearFilters()}
                  >
                    Reset Filters
                  </Button>
                </Box>
                {allValues.map((a, i) =>
                  <Box
                    className="col-6"
                    key={`box-${i}`}
                  >
                    <AximDropdown
                      key={`aximdropdown-${i}`}
                      label={a.header}
                      handleChange={e => changeFilter(e.target.value, a.column)}
                      value={a.column.filterValue === undefined ? '' : a.column.filterValue}
                      pairs={[{ value: '', text: "All" }, ...a.values.map((v, i) => { return { value: v } })]} />
                  </Box>
                )}
              </Box>
            </Box>
          </Popover>
        </Box>

      </Grid>
      <Stack key="b" className="px-1" direction="row" spacing={1}>
        {filterValues.map((f, i) => <Chip key={`chip-${i}`} label={f.value} variant="outlined" onDelete={() => handleDelete(f.id)} />)}
      </Stack>
    </>
  );
}

function DefaultColumnFilter ({
  column: { filterValue, preFilteredRows, setFilter },
}) {
  const count = preFilteredRows.length;

  return (
    //   <input
    //     className="form-control"
    //     value={filterValue || ""}
    //     onChange={(e) => {
    //       setFilter(e.target.value || undefined);
    //     }}
    //     placeholder={`Search ${count} records...`}
    //   />
    <></>
  );
}


function TableAllData ({ columns, data, tableType, tableColumn, initialState, isLoading, renderRowSubComponent, isAlwaysExpanded, headerContents, doShowBorders, hideFilter, hidePagination, download, downloadMethod, theadStyles, tbodyStyles }) {

  if (!initialState) {
    initialState = { pageIndex: 0, pageSize: 10 }
  }
  if (hidePagination) {
    initialState = { pageIndex: 0, pageSize: 100000 }
  }
  const ob = tableType === "invoices" ? "obWrapper" : "";
  const defaultColumn = React.useMemo(
    () => ({
      // Default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const dateSortFunction = useMemo(() => (a, b, columnId) => {
    var a1 = new Date(a.values[columnId]).getTime();
    var b1 = new Date(b.values[columnId]).getTime();
    return (a1 < b1 ? -1 : a1 > b1 ? 1 : 0);
  });

  const modifiedColumns = useMemo(() => {
    var mc = columns.map(c => {
      if (c.sortType === "dateSort") {
        c.sortType = dateSortFunction;
      }

      return c;
    })

    return mc;
  }, [columns])

  const downloadCSVFile = (csv_data) => {
    let CSVFile = new Blob([csv_data], { type: "text/csv" });

    // Create to temporary link to initiate
    // download process
    var temp_link = document.createElement('a');

    // Download csv file
    temp_link.download = `${download.prefix ?? "export"}_${moment().format('YYYY-MM-DD')}.csv`;
    var url = window.URL.createObjectURL(CSVFile);
    temp_link.href = url;

    // This link should not be displayed
    temp_link.style.display = "none";
    document.body.appendChild(temp_link);

    // Automatically click the link to trigger download
    temp_link.click();
    document.body.removeChild(temp_link);
  }
  const handleCSVDownload = () => {
    apiPost(apiUrls.postTableDownloadAudit(), { table: download.table ?? "unknown", url: window.location.href });

    if(downloadMethod instanceof Function) {
      downloadMethod();
    }
    else {
      var csv_data = assembleCSVData();

      downloadCSVFile(csv_data);
    }
  }
  
  const assembleCSVData = () => {
    var csv_data = [];

    var csvrow = [];
    for (var i = 0; i < headerGroups.length; i++) {
      for (var j = 0; j < headerGroups[i].headers.length; j++) {
        if (headerGroups[i].headers[j]?.style?.display !== "none") {
          csvrow.push(`\"${headerGroups[i].headers[j].Header}\"`);
        }
      }
    }

    if (csvrow.length > 0) {
      csv_data.push(csvrow.join(","));
    }

    for (var i = 0; i < rows.length; i++) {
      prepareRow(rows[i]);

      if (rows[i].cells) {
        var csvrow = [];
        for (var j = 0; j < rows[i].cells.length; j++) {
          if (rows[i].cells[j].column?.style?.display !== "none") {
            var cell = rows[i].cells[j].render("Cell");

            var cellStr = ReactDOMServer.renderToString(cell);
            var value = cellStr.replace(/(<([^>]+)>)/gi, "") // remove html tags
              .replace(/\n$/gi, ""); // remove trailing new line

            csvrow.push(`\"${value}\"`);
          }
        }
        
        if (csvrow.length > 0) {
          csv_data.push(csvrow.join(","));
        }
      }
    }

    csv_data = csv_data.join('\n');
    
    return csv_data;
  }

  let tableRef = React.createRef();
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    visibleColumns,
    state: { pageIndex, pageSize, isExpanded },
  } = useTable(
    {
      columns: modifiedColumns,
      data,
      defaultColumn,
      initialState: initialState,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination
  );
  return (
    <div className={`${TableStyle.allTableDataWrapper} ${ob}`}>
      {tableType === "invoices" || hideFilter || isLoading ? (
        <></>
      ) : (
        <GlobalFilter
          headerContents={headerContents}
          preGlobalFilteredRows={preGlobalFilteredRows}
          globalFilter={state.globalFilter}
          setGlobalFilter={setGlobalFilter}
          columns={visibleColumns}
          handleCSVDownload={handleCSVDownload}
          download={download}
        />
      )}

      <div className="responsive--table">

        <table ref={tableRef} className={`table tableall--wrapper ${doShowBorders ? "table-bordered" : ""} ${ob}`} {...getTableProps()}>
          <thead style={theadStyles}>
            {headerGroups.map((headerGroup, i) => (
              <tr key={`head-tr-${i}`} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, i) => {
                  //console.log(column,"column")
                  return (
                    <th key={`head-th-${i}`} {...column.getHeaderProps([
                      {
                        className: column.className,
                        style: column.style
                      },
                      column.getSortByToggleProps()
                    ])}>
                      {column.render("Header")}
                      {/* Add a sort direction indicator */}
                      {tableType === "invoices" ? <></> : <span>
                        {/* <ArrowDownwardIcon /> */}
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <ArrowDownwardIcon />
                          ) : (
                            <ArrowUpwardIcon />
                          )
                        ) : (
                          ""
                        )}
                      </span>}

                      {/* Render the columns filter UI */}
                      <div>
                        {column.canFilter ? column.render("Filter") : null}
                      </div>
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          {isLoading ?
            <tbody>
              <tr>
                <td colSpan="100%">
                  <LoaderAnimation />
                </td>
              </tr>
            </tbody>
            :
            <tbody {...getTableBodyProps()}  style={tbodyStyles}>
              {page.map((row, i) => {
                // console.log(row,'row')
                prepareRow(row);
                return (
                  <React.Fragment  key={`rf-${i}`}>
                    <tr key={`tr-${i}`} {...row.getRowProps()}

                    >
                      {row.cells.map((cell, i) => {
                        // console.log("CELL: ",cell)
                        return (
                          <td key={`td-${i}`} {...cell.getCellProps(
                            {
                              className: cell.column.cellClassName,
                              style: cell.column.cellStyle
                            })}>
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>

                    {row.isExpanded || isAlwaysExpanded ? (
                      <tr key={`expand-tr-${i}`}>
                        <td key={`expand-td-${i}`} colSpan={visibleColumns.length}>
                          {renderRowSubComponent && renderRowSubComponent({ row })}
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>}
          {footerGroups.some(g => g.headers.some(h => !(h.Footer instanceof Function))) ?
            <tfoot>
              {footerGroups.map((group, i) => (
                <tr key={`foot-tr-${i}`} {...group.getFooterGroupProps()}>
                  {group.headers.map((column, i) => (
                    <td key={`foot-td-${i}`} {...column.getFooterProps()}>{column.render('Footer')}</td>
                  ))}
                </tr>
              ))}
            </tfoot> : null}
        </table>
      </div>
      {/* Pagination can be built however you'd like. 
        This is just a very basic UI implementation: */}

      {tableType === "invoices" || hidePagination || isLoading ? (
        <></>
      ) : (
        <Box className="pagination">
          <Box sx={{ paddingLeft: "10px" }}>
            Records: {pageSize * pageIndex + 1} - {pageSize * pageIndex + page.length} / {rows.length}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Box component="span">
              Rows per page:
            </Box>
            <Box component="span">
              <select
                className="form-control rows-per-page-dd"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                }}
                style={{ width: "120px", height: "38px", "paddingTop": "0px" }}
              >
                {[5, 10, 20, 30, 40, 50].map((pageSize, i) => (
                  <option key={`option-${i}`} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </select>
            </Box>
            <Box component="span">
              <a className="page-link">
                Page: {pageIndex + 1} of {pageOptions.length}
              </a>
            </Box>
            <Box component="span"
              className="page-item"
              onClick={() => gotoPage(0)}
              disabled={!canPreviousPage}
            >
              <a className="page-link">
                <FirstPageIcon />
              </a>
            </Box>
            <Box component="span"
              className="page-item"
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
            >
              <a className="page-link">
                <KeyboardArrowLeftIcon />
              </a>
            </Box>
            <Box component="span"
              className="page-item"
              onClick={() => nextPage()}
              disabled={!canNextPage}
            >
              <a className="page-link">
                <KeyboardArrowRightIcon />
              </a>
            </Box>
            <Box component="span"
              className="page-item"
              onClick={() => gotoPage(pageCount - 1)}
              disabled={!canNextPage}
            >
              <a className="page-link">
                <LastPageIcon />
              </a>
            </Box>
          </Box>
        </Box>
      )}
    </div>
  );
}

export default TableAllData;
